/**
 * 🌡️ ImpactThermometer — Live progress bar toward the quarterly goal.
 *
 * Pulls from `/api/v1/public/campaign-goal`. Fill bar animates from 0
 * to its target width on first paint. Grows an emerald tick with a
 * subtle bounce as raised approaches the goal. Silent-fails when the
 * endpoint is offline OR when the SA marks the campaign inactive.
 *
 * SA-editable via `/admin/campaign-goal` (label / sublabel /
 * goal_dollars / deadline / active toggle).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Target, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/site`;

export default function ImpactThermometer() {
  const [data, setData] = useState(null);
  const [fillPct, setFillPct] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/v1/public/campaign-goal`, { withCredentials: false })
      .then((r) => {
        if (cancelled) return;
        if (!r.data?.active) { setData(null); return; }
        setData(r.data);
        requestAnimationFrame(() => setFillPct(r.data.pct || 0));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  const gap = Math.max(0, (data.goal_dollars || 0) - (data.raised_dollars || 0));
  const deadlineLabel = data.deadline
    ? new Date(data.deadline).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <section
      className="py-14 md:py-20 bg-[#FAFAF7] border-y border-[#0A0612]/5"
      data-testid="impact-thermometer"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-24">
        <div className="flex items-center gap-4 mb-6">
          <div className="h-12 w-12 rounded-full bg-[#1B4332] text-[#F4D88A] grid place-items-center flex-shrink-0">
            <Target className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#C9A227] mb-1">
              Campaign Goal · Live Progress
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-[#0A0612] leading-tight">
              {data.label}
            </h2>
            {data.sublabel && (
              <p className="text-sm md:text-base text-[#0A0612]/60 mt-2 max-w-2xl leading-relaxed">
                {data.sublabel}
              </p>
            )}
          </div>
        </div>

        {/* Thermometer bar */}
        <div className="mt-8" data-testid="thermometer-bar-wrap">
          {/* Numbers row */}
          <div className="flex items-baseline justify-between mb-3 flex-wrap gap-3">
            <div className="font-display text-4xl md:text-5xl font-bold tabular-nums text-[#0A0612]" data-testid="thermometer-raised">
              ${(data.raised_dollars || 0).toLocaleString()}
              <span className="text-lg md:text-xl font-normal text-[#0A0612]/50 ml-3">
                raised of ${(data.goal_dollars || 0).toLocaleString()}
              </span>
            </div>
            <div className="text-right">
              <div className="font-display text-2xl md:text-3xl font-bold tabular-nums text-[#1B4332]" data-testid="thermometer-pct">
                {data.pct}%
              </div>
              {deadlineLabel && (
                <div className="font-mono text-[9px] tracking-[0.22em] uppercase text-[#6B6F76] mt-1">
                  by {deadlineLabel}
                </div>
              )}
            </div>
          </div>

          {/* Bar */}
          <div className="relative h-6 md:h-7 bg-[#0A0612]/8 rounded-full overflow-hidden shadow-inner">
            <div
              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#1B4332] via-[#059669] to-[#10B981] transition-all duration-1000 ease-out rounded-full"
              style={{ width: `${fillPct}%` }}
              data-testid="thermometer-fill"
            />
            {/* Shimmering highlight strip on top of the fill */}
            <div
              className="absolute inset-y-0 left-0 rounded-full pointer-events-none"
              style={{
                width: `${fillPct}%`,
                background: "linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0) 55%)",
                transition: "width 1s ease-out",
              }}
            />
          </div>

          {/* Gap + CTA */}
          <div className="mt-5 flex items-center justify-between flex-wrap gap-4">
            <div className="text-sm text-[#0A0612]/70" data-testid="thermometer-gap">
              {gap > 0 ? (
                <>
                  <span className="font-bold text-[#0A0612]">${gap.toLocaleString()}</span>{" "}
                  <span className="text-[#0A0612]/60">to close the gap.</span>
                </>
              ) : (
                <span className="font-bold text-emerald-700">Goal reached — thank you.</span>
              )}
            </div>
            <a
              href="/donate"
              data-testid="thermometer-cta"
              className="inline-flex items-center gap-2 bg-[#0A0612] text-[#F4D88A] px-5 py-3 font-mono text-[10px] tracking-[0.22em] uppercase hover:bg-[#1B4332] transition-colors"
            >
              Push The Bar Forward <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
